import { useState } from 'react';
import { createCuratorOverride } from '../repositories/adminStoryRepository';
import { ModalOverlay } from './ModalOverlay';
import {
  CURATOR_OVERRIDE_DESCRIPTIONS,
  CURATOR_OVERRIDE_LABELS,
  type CuratorOverrideActionType,
} from '../utils/curatorOverride';

interface CuratorOverrideModalProps {
  overrideType: CuratorOverrideActionType;
  articleId: string;
  articleTitle: string;
  /** For dupe overrides: the other article */
  targetArticleId?: string;
  targetArticleTitle?: string;
  /** For topic overrides: the current topic */
  currentTopic?: string;
  onClose: () => void;
  onSaved?: () => void;
}

export function CuratorOverrideModal({
  overrideType,
  articleId,
  articleTitle,
  targetArticleId,
  targetArticleTitle,
  currentTopic,
  onClose,
  onSaved,
}: CuratorOverrideModalProps) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createCuratorOverride({
        override_type: overrideType,
        article_id: articleId,
        target_article_id: targetArticleId,
        topic: currentTopic,
        note: note.trim() || undefined,
      });
      setDone(true);
      onSaved?.();
      setTimeout(onClose, 600);
    } catch {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} className="score-feedback-modal">
        <div className="score-feedback-modal-title">
          {CURATOR_OVERRIDE_LABELS[overrideType]}
        </div>
        <div className="score-feedback-modal-article">{articleTitle}</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginBottom: 8 }}>
          {CURATOR_OVERRIDE_DESCRIPTIONS[overrideType]}
        </div>
        {targetArticleTitle && (
          <div style={{ fontSize: '0.82rem', marginBottom: 8 }}>
            <span style={{ color: 'var(--gray-500)' }}>Target: </span>
            <span style={{ fontWeight: 500 }}>{targetArticleTitle}</span>
          </div>
        )}
        {currentTopic && (
          <div style={{ fontSize: '0.82rem', marginBottom: 8 }}>
            <span style={{ color: 'var(--gray-500)' }}>Topic: </span>
            <span style={{ fontWeight: 500 }}>{currentTopic}</span>
          </div>
        )}
        {done ? (
          <div className="score-feedback-modal-done">Saved!</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <textarea
              className="score-feedback-comment"
              placeholder="Optional note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              autoFocus
            />
            <div className="score-feedback-modal-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Saving...' : 'Confirm'}
              </button>
              <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        )}
    </ModalOverlay>
  );
}
